import React, { useMemo, useRef } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  BarController,
  LineController,
  PieController,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar, Pie } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  BarController,
  LineController,
  PieController,
  Title,
  Tooltip,
  Legend
);

/**
 * Parse a pipe-delimited markdown table string into headers and rows.
 * Mirrors the logic in FinancialTable for consistency.
 */
export const parseTableString = (tableString) => {
  try {
    if (!tableString) return null;

    const normalizeLine = (line) => {
      const normalized = line.replace(/^\s*[-*]\s+(?=\|)/, '').trim();
      if (normalized.startsWith('|') && !normalized.endsWith('|')) {
        return `${normalized} |`;
      }
      return normalized;
    };

    const lines = tableString.trim().split('\n').map(normalizeLine);

    // Filter out separator lines (e.g. |---|---|)
    const contentLines = lines.filter((line) => {
      const trimmed = line.trim();
      return !/^\|[\s\-:|]+\|$/.test(trimmed);
    });

    if (contentLines.length < 2) return null;

    const parseRow = (rowStr) => {
      const inner = normalizeLine(rowStr).replace(/^\||\|$/g, '');
      return inner.split('|').map((cell) => cell.trim());
    };

    const headers = parseRow(contentLines[0]);
    const rows = contentLines.slice(1).map((line) => {
      let cells = parseRow(line);
      if (cells.length < headers.length) {
        const missing = headers.length - cells.length;
        cells = [...cells, ...new Array(missing).fill('-')];
      }
      if (cells.length > headers.length) {
        cells = cells.slice(0, headers.length);
      }
      return cells.map((cell) => {
        const clean = cell.replace(/\*\*/g, '').trim();
        if (clean === '-' || clean === '' || clean.toLowerCase() === 'null') {
          return null;
        }
        return clean;
      });
    });

    return { headers, rows };
  } catch (err) {
    console.error('DataChart parsing error:', err);
    return null;
  }
};

const isNumericCell = (val) => {
  if (val === null || val === undefined) return false;
  // Report cells commonly include units, ranges, and explanatory text, e.g.
  // "USD 131.3 billion" or "USD 263.46–439 billion". A cell is chartable
  // when it contains at least one numeric value; extractNumber uses the same
  // rule below so detection and plotting stay consistent.
  return /[-+]?(?:\d{1,3}(?:,\d{3})+|\d+(?:\.\d+)?|\.\d+)/.test(String(val));
};

const extractNumber = (val) => {
  if (val === null || val === undefined) return 0;
  const match = String(val).match(/[-+]?(?:\d{1,3}(?:,\d{3})+|\d+(?:\.\d+)?|\.\d+)/);
  if (!match) return 0;
  const num = parseFloat(match[0].replace(/,/g, ''));
  return isNaN(num) ? 0 : num;
};

/**
 * DataChart
 *
 * Renders a Bar or Pie chart from a pipe-delimited markdown table.
 * - Bar chart: multiple data rows (categories on X-axis, numeric columns as datasets)
 * - Pie chart: single data row with multiple numeric columns (proportional breakdown)
 */
const DataChart = ({ tableString, isDarkMode, isExport = false }) => {
  const chartMeta = useMemo(() => {
    const data = parseTableString(tableString);
    if (!data) return null;

    const { headers, rows } = data;
    if (!rows || rows.length === 0) return null;

    // Identify numeric columns (skip the first column — usually the label/category)
    let numericCols = [];
    for (let c = 1; c < headers.length; c++) {
      const numericCount = rows.filter((r) => isNumericCell(r[c])).length;
      if (numericCount > 0) numericCols.push(c);
    }

    // For single-row tables where the first cell is also numeric and there are
    // no date-like headers, it's likely a transposed table with no label column.
    const isSingleRow = rows.length === 1;
    if (isSingleRow && isNumericCell(rows[0][0]) && headers.length >= 3) {
      const hasDateLikeHeader = headers.some((h) =>
        /year|date|time|period|id/i.test(h)
      );
      if (!hasDateLikeHeader) {
        numericCols = [];
        for (let c = 0; c < headers.length; c++) {
          if (isNumericCell(rows[0][c])) numericCols.push(c);
        }
      }
    }

    if (numericCols.length === 0) return null;

    // Chart heuristics
    const hasMultipleNumericCols = numericCols.length >= 2;

    let type = null;
    if (isSingleRow && hasMultipleNumericCols) {
      type = 'pie';
    } else if (rows.length >= 1 && numericCols.length >= 1) {
      // Prefer bar for multi-row; also fallback for single-row with 1 numeric col
      type = 'bar';
    }

    if (!type) return null;

    const textColor = isDarkMode ? '#e2e8f0' : '#1e293b';
    const gridColor = isDarkMode ? '#334155' : '#e2e8f0';

    const palette = [
      'rgba(59, 130, 246, 0.85)',
      'rgba(16, 185, 129, 0.85)',
      'rgba(249, 115, 22, 0.85)',
      'rgba(139, 92, 246, 0.85)',
      'rgba(236, 72, 153, 0.85)',
      'rgba(234, 179, 8, 0.85)',
      'rgba(6, 182, 212, 0.85)',
      'rgba(244, 63, 94, 0.85)',
    ];

    const borderPalette = palette.map((c) => c.replace('0.85', '1'));

    const baseOptions = {
      responsive: true,
      maintainAspectRatio: false,
      animation: isExport ? false : undefined,
      plugins: {
        legend: {
          position: isExport ? 'top' : undefined,
          labels: { color: textColor, font: { size: isExport ? 12 : 12 }, boxWidth: isExport ? 20 : undefined },
        },
        title: { display: false },
      },
    };

    if (type === 'bar') {
      const labels = rows.map((r) => r[0] || `Row`);

      // Detect percentage columns (CAGR, %, Rate, etc.)
      const isPercentageCol = (colIdx) => {
        const header = String(headers[colIdx]).toLowerCase();
        if (/%|cagr|rate|growth|percent|share|margin/i.test(header)) return true;
        const pctCount = rows.filter((r) => String(r[colIdx]).includes('%')).length;
        return pctCount > rows.length / 2;
      };

      const hasPctCols = numericCols.some(isPercentageCol);
      const hasAbsCols = numericCols.some((c) => !isPercentageCol(c));
      const needsDualAxis = hasPctCols && hasAbsCols;

      const datasets = numericCols.map((colIdx, i) => {
        const label = headers[colIdx]?.replace(/\*\*/g, '') || `Series ${i + 1}`;
        const dataPoints = rows.map((r) => extractNumber(r[colIdx]));
        const isPct = isPercentageCol(colIdx);

        if (needsDualAxis && isPct) {
          // Percentage columns render as a line on the secondary axis
          const pctColor = isDarkMode ? '#fb923c' : '#ea580c';

          return {
            type: 'line',
            label,
            data: dataPoints,
            borderColor: pctColor,
            backgroundColor: pctColor,
            borderWidth: 2,
            pointRadius: 4,
            pointHoverRadius: 6,
            pointBackgroundColor: isDarkMode ? '#0f172a' : '#ffffff',
            pointBorderWidth: 2,
            tension: 0.3,
            yAxisID: 'y1',
          };
        }

        return {
          type: 'bar',
          label,
          data: dataPoints,
          backgroundColor: palette[i % palette.length],
          borderColor: borderPalette[i % borderPalette.length],
          borderWidth: 1,
          borderRadius: 4,
          yAxisID: 'y',
        };
      });

      const scales = {
        x: {
          ticks: { color: textColor, font: { size: isExport ? 10 : 11 } },
          grid: { color: gridColor },
        },
        y: {
          type: 'linear',
          display: true,
          position: 'left',
          ticks: { color: textColor, font: { size: isExport ? 10 : 11 } },
          grid: { color: gridColor },
          title: {
            display: needsDualAxis && hasAbsCols,
            text: 'Value',
            color: textColor,
            font: { size: 10 },
          },
        },
      };

      if (needsDualAxis) {
        const pctColor = isDarkMode ? '#fb923c' : '#ea580c'; // orange-400 / orange-600
        scales.y1 = {
          type: 'linear',
          display: true,
          position: 'right',
          ticks: {
            color: pctColor,
            font: { size: isExport ? 10 : 11 },
            callback: (v) => v + '%',
          },
          grid: { drawOnChartArea: false },
          title: {
            display: true,
            text: 'Percentage',
            color: pctColor,
            font: { size: 10 },
          },
        };
      }

      return {
        type,
        data: { labels, datasets },
        options: {
          ...baseOptions,
          scales,
        },
      };
    }

    // Pie chart
    const pieLabels = numericCols.map(
      (c) => headers[c]?.replace(/\*\*/g, '') || `Item ${c}`
    );
    const pieData = numericCols.map((c) => extractNumber(rows[0][c]));

    return {
      type,
      data: {
        labels: pieLabels,
        datasets: [
          {
            data: pieData,
            backgroundColor: palette.slice(0, pieData.length),
            borderColor: isDarkMode ? '#0f172a' : '#ffffff',
            borderWidth: 2,
          },
        ],
      },
      options: {
        ...baseOptions,
        plugins: {
          ...baseOptions.plugins,
          tooltip: {
            callbacks: {
              label: (context) => {
                const label = context.label || '';
                const value = context.parsed;
                const total = context.dataset.data.reduce((a, b) => a + b, 0);
                const pct = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
                const suffix = String(rows[0][numericCols[context.dataIndex]]).includes('%')
                  ? '%'
                  : '';
                return `${label}: ${value}${suffix} (${pct}%)`;
              },
            },
          },
        },
      },
    };
  }, [tableString, isDarkMode, isExport]);

  const chartRef = useRef(null);

  const handleDownload = () => {
    const canvas = chartRef.current?.canvas;
    if (!canvas) return;

    // Chart.js canvas is transparent by default; composite onto a solid background for JPG
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = canvas.width;
    tempCanvas.height = canvas.height;
    const ctx = tempCanvas.getContext('2d');
    ctx.fillStyle = isDarkMode ? '#0f172a' : '#ffffff';
    ctx.fillRect(0, 0, tempCanvas.width, tempCanvas.height);
    ctx.drawImage(canvas, 0, 0);

    const url = tempCanvas.toDataURL('image/jpeg', 0.92);
    const randomName = `chart-${Math.random().toString(36).substring(2, 10)}.jpg`;
    const link = document.createElement('a');
    link.href = url;
    link.download = randomName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (!chartMeta) return null;

  const ChartComponent = chartMeta.type === 'bar' ? Bar : Pie;

  return (
    <div
      className={`mt-4 p-4 rounded-xl border ${
        isDarkMode
          ? 'bg-slate-800/60 border-slate-700'
          : 'bg-white border-slate-200'
      } ${isExport ? 'shadow-sm break-inside-avoid' : ''}`}
    >
      <div className="flex items-center justify-between mb-3">
        <p
          className={`text-xs font-semibold uppercase tracking-wider ${
            isDarkMode ? 'text-slate-400' : 'text-slate-500'
          }`}
        >
          {chartMeta.type === 'bar' ? 'Bar Chart' : 'Pie Chart'}
        </p>
        {!isExport && (
          <button
            onClick={handleDownload}
            title="Download chart as image"
            className={`p-1.5 rounded-md transition-colors ${
              isDarkMode
                ? 'text-slate-400 hover:text-slate-200 hover:bg-slate-700'
                : 'text-slate-500 hover:text-slate-800 hover:bg-slate-100'
            }`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
          </button>
        )}
      </div>
      <div className={`relative ${isExport ? 'h-[460px]' : 'h-64'}`}>
        <ChartComponent ref={chartRef} data={chartMeta.data} options={chartMeta.options} />
      </div>
    </div>
  );
};

export default DataChart;
